import { SEARCE_COLORS } from '../../../theme';
import { ProjectDropdownOption } from '../../../services/api';
import { RequestType, LevelType, FormData } from './types';
import { hasValidRecipientEmails } from './emailRecipients';

/** Max tech stack entries on a solution story. Must match the backend validator. */
export const TECH_STACK_MAX_ITEMS = 6;

/** Tracker statuses that count as "closed" for sign-off, feedback, solution story, etc. */
const CLOSED_LIKE_STATUSES = new Set(['closed', 'sign off', 'completed']);

const normalizeStatusKey = (raw: string | null | undefined): string =>
  String(raw || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');

const isClosedLikeProjectStatus = (status: string | null | undefined): boolean =>
  CLOSED_LIKE_STATUSES.has(normalizeStatusKey(status));

// Project status indicator colors: Green = closed-like (Sign Off, Completed, …), Blue = In progress, Amber = Yet to Start
export const getProjectStatusColor = (status: string | null | undefined): string => {
  if (!status) return SEARCE_COLORS.warning; // default to amber for unknown
  if (isClosedLikeProjectStatus(status)) return SEARCE_COLORS.success;
  const s = normalizeStatusKey(status);
  if (s === 'in progress' || s.replace(/\s/g, '') === 'inprogress') return SEARCE_COLORS.primary;
  if (s === 'yet to start' || s.replace(/\s/g, '') === 'yettostart') return SEARCE_COLORS.warning;
  if (s === 'on hold' || s.replace(/\s/g, '') === 'onhold') return SEARCE_COLORS.warning;
  return SEARCE_COLORS.warning;
};

export const filterProjectsByStatus = (
  projects: ProjectDropdownOption[],
  statusFilter: string | null
): ProjectDropdownOption[] => {
  if (!statusFilter || statusFilter === 'All') return projects;
  const normalized = normalizeStatusKey(statusFilter);
  const compact = normalized.replace(/\s/g, '');

  // UI chip "Closed" = eligible for post-close workflows (Sign Off, Completed, legacy Closed, …)
  if (normalized === 'closed' || compact === 'closed') {
    const out = projects.filter((p) => isClosedLikeProjectStatus(p.status));
    return out;
  }

  return projects.filter((p) => {
    const s = normalizeStatusKey(p.status);
    const c = s.replace(/\s/g, '');
    return s === normalized || c === compact;
  });
};

// Get form title based on request type
export const getFormTitle = (selectedRequestType: RequestType): string => {
  switch (selectedRequestType) {
    case 'feedback':
      return 'Feedback and Sign Off';
    case 'appreciation':
      return 'Customer Appreciation Form';
    case 'escalation':
      return 'Customer Escalation Form';
    case 'solution-story':
      return 'Solution Story Form';
    default:
      return 'New Request Form';
  }
};

// Get level label
export const getLevelLabel = (selectedLevel: LevelType): string => {
  return selectedLevel === 'project' ? 'Project' : 'Individual';
};

/** True if the first 3 items (indices 0,1,2) are non-empty; 4th can be empty. */
export const isSolutionStoryFirstThreeFilled = (arr: string[] | undefined): boolean => {
  const a = arr || ['', '', '', ''];
  return Boolean(a[0]?.trim() && a[1]?.trim() && a[2]?.trim());
};

/** True when solution story array fields (business context, solution, impact) all have first 3 filled. */
export const isSolutionStoryArraysValid = (formData: FormData): boolean => {
  return (
    isSolutionStoryFirstThreeFilled(formData.businessContext) &&
    isSolutionStoryFirstThreeFilled(formData.solution) &&
    isSolutionStoryFirstThreeFilled(formData.impact)
  );
};

// Form validation
export const validateForm = (
  selectedRequestType: RequestType,
  formData: FormData,
  selectedLevel: LevelType | null,
  isEscalationResolved: boolean
): string | null => {
  if (selectedRequestType === 'feedback') {
    if (!formData.closureReport) {
      return 'Please upload a Closure Report file';
    }
    if (!hasValidRecipientEmails(formData.toEmails)) {
      return 'TO Email addresses are required for feedback requests';
    }
  } else if (selectedRequestType === 'solution-story') {
    if (!formData.projectCode.trim()) {
      return 'Please select a project';
    }
    if (!formData.solutionProvided.trim()) {
      return 'Solution Provided is required';
    }
    if (formData.solutionProvided.length > 70) {
      return 'Solution Provided must be 70 characters or less';
    }
    if (!formData.keyObjectivesChallenges.trim()) {
      return 'Key Objectives/Challenges is required';
    }
    if (formData.keyObjectivesChallenges.length < 400) {
      return 'Key Objectives/Challenges must be at least 400 characters';
    }
    if (!formData.executiveSummary.trim()) {
      return 'Executive Summary is required';
    }
    if (formData.executiveSummary.length > 150) {
      return 'Executive Summary must be 150 characters or less';
    }
    const challengesLen = (formData.challengesChangedSinceInception || '').trim().length;
    if (challengesLen < 250) {
      return 'Challenges & whether these changed since inception must be at least 250 characters';
    }
    const solutionDescLen = (formData.solutionDescription || '').trim().length;
    if (solutionDescLen < 400) {
      return 'Solution description must be at least 400 characters';
    }
    if (!formData.practice) {
      return 'Practice is required';
    }
    const platformUsageLen = (formData.platformUsageWay || '').trim().length;
    if (platformUsageLen < 100) {
      return 'In what way was the platform used? must be at least 100 characters';
    }
    // Validate Business Context: first 3 points required, 4th optional
    if (!isSolutionStoryFirstThreeFilled(formData.businessContext)) {
      return 'Business Context: First 3 points are required (4th is optional)';
    }
    const businessContextTotalChars = (formData.businessContext || []).reduce((sum, val) => sum + (val?.length || 0), 0);
    if (businessContextTotalChars > 358) {
      return 'Business Context: Total characters cannot exceed 358';
    }
    // Validate Solution: first 3 points required, 4th optional
    if (!isSolutionStoryFirstThreeFilled(formData.solution)) {
      return 'Solution: First 3 points are required (4th is optional)';
    }
    const solutionTotalChars = (formData.solution || []).reduce((sum, val) => sum + (val?.length || 0), 0);
    if (solutionTotalChars > 358) {
      return 'Solution: Total characters cannot exceed 358';
    }
    // Validate Impact: first 3 points required, 4th optional
    if (!isSolutionStoryFirstThreeFilled(formData.impact)) {
      return 'Impact: First 3 points are required (4th is optional)';
    }
    const impactTotalChars = (formData.impact || []).reduce((sum, val) => sum + (val?.length || 0), 0);
    if (impactTotalChars > 358) {
      return 'Impact: Total characters cannot exceed 358';
    }
    const detailedImpactLen = (formData.detailedSolutionImpact || '').trim().length;
    if (detailedImpactLen < 400) {
      return 'Detailed solution impact must be at least 400 characters';
    }
    const techUsageLen = (formData.techUsageWay || '').trim().length;
    if (techUsageLen < 100) {
      return 'In what way was each specific technology used must be at least 100 characters';
    }
    if (!formData.referencable) {
      return 'Referencable / Non-referenceable is required';
    }
    // Validate Tech Stack
    const techStackList = formData.techStack || [];
    if (techStackList.length > TECH_STACK_MAX_ITEMS) {
      return `Tech Stack: Maximum ${TECH_STACK_MAX_ITEMS} items allowed`;
    }
    const techStackFilled = techStackList.filter(val => val?.trim()).length;
    if (techStackFilled === 0) {
      return 'Tech Stack: At least one entry is required';
    }
    // Validate Industry Group
    if (!formData.industryGroup) {
      return 'Industry Group is required';
    }
    // Validate Industry
    if (!formData.industry?.trim()) {
      return 'Industry is required';
    }
  } else if (selectedRequestType === 'escalation') {
    if (!selectedLevel) {
      return 'Please select a level (Project or Individual)';
    }
    if (!formData.issueDescription.trim()) {
      return 'Issue description is required';
    }
    if (!formData.dateRaised) {
      return 'Date raised is required';
    }
    const todayStr = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD
    if (formData.dateRaised > todayStr) {
      return 'Date raised cannot be a future date';
    }
    if (selectedLevel === 'project') {
      if (!formData.escalatedBySource) {
        return 'Please select who escalated (Client or Searce)';
      }
      if (!formData.escalatedByName.trim()) {
        return formData.escalatedBySource === 'client'
          ? 'Client name is required'
          : 'Escalated by name is required';
      }
      if (!formData.escalatedByDesignation.trim()) {
        return formData.escalatedBySource === 'client'
          ? 'Client designation is required'
          : 'Escalated by designation is required';
      }
    }
    if (selectedLevel === 'individual') {
      if (!formData.escalatedByName.trim()) {
        return 'Escalated by name is required';
      }
      if (!formData.escalatedByDesignation.trim()) {
        return 'Escalated by designation is required';
      }
      if (!formData.projectMemberName.trim()) {
        return 'Project member name is required for individual-level escalations';
      }
    }
    if (isEscalationResolved) {
      if (!formData.dateResolved) {
        return 'Date resolved is required when escalation is resolved';
      }
      if (formData.dateResolved > todayStr) {
        return 'Date resolved cannot be a future date';
      }
      if (formData.dateResolved < formData.dateRaised) {
        return 'Date resolved must be on or after the date raised';
      }
      if (!formData.solutionProposed.trim()) {
        return 'Solution proposed is required when escalation is resolved';
      }
      if (!formData.rootCause.trim()) {
        return 'Root cause is required when escalation is resolved';
      }
      if (!formData.learnings.trim()) {
        return 'Learnings are required when escalation is resolved';
      }
    }
  } else if (selectedRequestType === 'appreciation') {
    if (!selectedLevel) {
      return 'Please select a level (Project or Individual)';
    }
    if (!formData.feedbackSnippet.trim()) {
      return 'Feedback/Appreciation content is required';
    }
    if (selectedLevel === 'individual' && !formData.projectMemberName.trim()) {
      return 'Project member name is required for individual-level appreciations';
    }
    if (!formData.appreciationFile) {
      return 'Please upload the Submit Customer Sign-Off file (PDF copy of feedback email)';
    }
  }
  return null;
};
